/**
 * API client do app `crm/` (pipeline de Leads).
 *
 * Conceito: o CRM e um board kanban onde cada card e um LEAD e cada coluna
 * e uma ETAPA configuravel do pipeline. "Gerar orcamento" e apenas uma etapa
 * (tipo ORCAMENTO) — nunca chamamos o card de "orcamento".
 *
 * Endpoints (sem trailing slash, padrao do projeto):
 *   GET    /api/crm/leads/board           board agrupado por etapa (filtros aplicaveis)
 *   GET    /api/crm/leads                 lista flat (mesmos filtros)
 *   GET    /api/crm/leads/{id}            LeadDetalhe (+ eventos, orcamentos, etc)
 *   POST   /api/crm/leads                 cria lead -> 201 LeadDetalhe
 *   POST   /api/crm/leads/{id}/mover      {etapa_destino, razao_perda?, descricao_perda?, criar_orcamento?}
 *   POST   /api/crm/leads/{id}/arquivar
 *   POST   /api/crm/leads/{id}/desarquivar
 *   POST   /api/crm/leads/{id}/atribuir   {responsavel}
 *   GET    /api/crm/leads/exportar        linhas completas para exportar (mesmos filtros)
 *
 *   GET/POST/PATCH/DELETE  /api/crm/etapas-pipeline
 *   POST   /api/crm/etapas-pipeline/reordenar   {ordem_ids: [id,...]}
 *
 *   GET    /api/crm/pipeline              {etapas:[...], regras:{...}}
 *   POST   /api/crm/pipeline/configurar   {etapas:[...], regras:{...}} -> {etapas, regras, leads_remapeados}
 *
 * Filtros aceitos por board/list: busca, etapa, responsavel, origem, meus=true.
 * NOTA: o filtro "meus" e resolvido pelo backend via request.user — NAO envie
 * o id do usuario, apenas `meus=true`.
 */

const PREFIXO_LEAD = "/api/crm/leads";
const PREFIXO_ETAPA = "/api/crm/etapas-pipeline";
const PREFIXO_PIPELINE = "/api/crm/pipeline";
const PREFIXO_NOTA = "/api/crm/notas";
const PREFIXO_TAREFA = "/api/crm/tarefas";
const PREFIXO_SUBTAREFA = "/api/crm/subtarefas";
const PREFIXO_ANEXO = "/api/crm/anexos";
const PREFIXO_COMPROMISSO = "/api/crm/compromissos";

// ============================================================
// Constantes compartilhadas (selects + fallback)
// ============================================================

// Categorias de etapa (contrato fixo). A semantica nova trata as etapas como
// status do negocio: PENDENTE (entrada) -> EM_ANDAMENTO (intermediarias) ->
// FINALIZADO (convertido, vira cliente no backend) / PARADO (pausa, sem perda).
// `tone` mapeia direto na paleta funcional do design system.
export const TIPOS_ETAPA = [
  { value: "PENDENTE", label: "Pendente", tone: "neutral" },
  { value: "EM_ANDAMENTO", label: "Em andamento", tone: "info" },
  { value: "FINALIZADO", label: "Finalizado", tone: "success" },
  { value: "PARADO", label: "Parado", tone: "warning" },
];

export const TIPO_ETAPA_LABEL = TIPOS_ETAPA.reduce((acc, t) => {
  acc[t.value] = t.label;
  return acc;
}, {});

// Origens canonicas do lead (espelha o backend; OUTRO no fim).
// ESPELHA crm.models.ORIGENS_LEAD (backend): so o que pode ser ESCOLHIDO.
// LOJA e EVENTO eram um segundo codigo para o mesmo canal (Loja fisica,
// Feira) e a migration crm/0018 converteu o que estava gravado; o backend
// recusa os dois com 400 (CRM-30). Ficam em ORIGENS_LEAD_LEGADAS so para
// dar rotulo a dado que ainda nao passou pela migration.
export const ORIGENS_LEAD = [
  { value: "INDICACAO", label: "Indicação" },
  { value: "SITE", label: "Site" },
  { value: "INSTAGRAM", label: "Instagram" },
  { value: "WHATSAPP", label: "WhatsApp" },
  { value: "FACEBOOK", label: "Facebook" },
  { value: "GOOGLE", label: "Google / busca" },
  { value: "LOJA_FISICA", label: "Loja física" },
  { value: "TELEFONE", label: "Telefone" },
  { value: "ARQUITETO", label: "Arquiteto / parceiro" },
  { value: "FEIRA", label: "Feira / evento" },
  { value: "OUTRO", label: "Outro" },
];

// So rotulo de exibicao: nao entram em select nenhum.
export const ORIGENS_LEAD_LEGADAS = [
  { value: "LOJA", label: "Loja / presencial" },
  { value: "EVENTO", label: "Evento / feira" },
];

export const ORIGEM_LEAD_LABEL = [...ORIGENS_LEAD, ...ORIGENS_LEAD_LEGADAS].reduce(
  (acc, o) => {
    acc[o.value] = o.label;
    return acc;
  },
  {}
);

/** A origem pode ser escolhida hoje (nao e legada nem vazia)? */
export function origemEscolhivel(origem) {
  return ORIGENS_LEAD.some((o) => o.value === origem);
}

// Qualificacao do lead (cockpit). Espelha as choices do backend
// (LeadAtualizarSerializer). Valor "" = sem qualificacao definida.
export const TEMPERATURAS = [
  { value: "QUENTE", label: "Quente", tone: "danger" },
  { value: "MORNO", label: "Morno", tone: "warning" },
  { value: "FRIO", label: "Frio", tone: "info" },
];

export const TEMPERATURA_LABEL = TEMPERATURAS.reduce((acc, t) => {
  acc[t.value] = t.label;
  return acc;
}, {});

export const TEMPERATURA_TONE = TEMPERATURAS.reduce((acc, t) => {
  acc[t.value] = t.tone;
  return acc;
}, {});

export const STATUS_LEAD = [
  { value: "NOVO", label: "Novo", tone: "neutral" },
  { value: "CONTATADO", label: "Contatado", tone: "info" },
  { value: "QUALIFICADO", label: "Qualificado", tone: "success" },
  { value: "NUTRINDO", label: "Nutrindo", tone: "warning" },
  { value: "DESCARTADO", label: "Descartado", tone: "danger" },
];

export const STATUS_LEAD_LABEL = STATUS_LEAD.reduce((acc, s) => {
  acc[s.value] = s.label;
  return acc;
}, {});

export const STATUS_LEAD_TONE = STATUS_LEAD.reduce((acc, s) => {
  acc[s.value] = s.tone;
  return acc;
}, {});

// Motivos de perda do lead (botao "Registrar perda" do cockpit). Espelha
// MOTIVOS_PERDA_LEAD do backend (crm.models). `grupo` agrupa visualmente no
// <select> (optgroups). "OUTRO" exige o campo de detalhe preenchido; para os
// demais o detalhe e opcional (mas sempre visivel).
export const MOTIVO_PERDA_OUTRO = "OUTRO";

export const MOTIVOS_PERDA = [
  { value: "PRECO_ACIMA", label: "Preço acima do orçamento do cliente", grupo: "Preço / financeiro" },
  { value: "ACHOU_CARO", label: "Achou caro / fora do esperado", grupo: "Preço / financeiro" },
  { value: "CONCORRENTE_PRECO", label: "Concorrente com preço menor", grupo: "Preço / financeiro" },
  { value: "SEM_CREDITO", label: "Não conseguiu crédito / financiamento", grupo: "Preço / financeiro" },
  { value: "CONDICOES_PAGAMENTO", label: "Condições de pagamento não atenderam", grupo: "Preço / financeiro" },
  { value: "CONCORRENTE", label: "Fechou com concorrente (não por preço)", grupo: "Concorrência" },
  { value: "MARCENEIRO_PROPRIO", label: "Vai fazer com marceneiro próprio / conhecido", grupo: "Concorrência" },
  { value: "COMPROU_PRONTO", label: "Comprou móvel pronto / de loja", grupo: "Concorrência" },
  { value: "PRAZO_LONGO", label: "Prazo de entrega muito longo", grupo: "Prazo" },
  { value: "SEM_AGENDA", label: "Sem disponibilidade na nossa agenda", grupo: "Prazo" },
  { value: "NAO_GOSTOU_PROJETO", label: "Não gostou do projeto / 3D", grupo: "Projeto / escopo" },
  { value: "ESCOPO_INADEQUADO", label: "Escopo / medidas não atenderam", grupo: "Projeto / escopo" },
  { value: "REDUZIU_ESCOPO", label: "Reduziu o escopo (fez menos ambientes)", grupo: "Projeto / escopo" },
  { value: "DESISTIU", label: "Cliente desistiu do projeto", grupo: "Timing / decisão" },
  { value: "OBRA_ADIADA", label: "Obra / reforma adiada", grupo: "Timing / decisão" },
  { value: "MUDOU_IDEIA", label: "Mudou de ideia sobre o ambiente", grupo: "Timing / decisão" },
  { value: "DECISAO_ADIADA", label: "Decisão adiada sem previsão", grupo: "Timing / decisão" },
  { value: "SEM_APROVACAO", label: "Sem aprovação da família / sócio", grupo: "Timing / decisão" },
  { value: "SUMIU", label: "Cliente sumiu / sem resposta", grupo: "Outros" },
  { value: "SO_PESQUISANDO", label: "Estava apenas pesquisando preço", grupo: "Outros" },
  { value: "ATENDIMENTO", label: "Problema no atendimento / experiência", grupo: "Outros" },
  { value: MOTIVO_PERDA_OUTRO, label: "Outro motivo", grupo: "Outros" },
];

export const MOTIVO_PERDA_LABEL = MOTIVOS_PERDA.reduce((acc, m) => {
  acc[m.value] = m.label;
  return acc;
}, {});

// Motivos agrupados (na ordem de aparicao) para montar optgroups no select.
export const MOTIVOS_PERDA_POR_GRUPO = MOTIVOS_PERDA.reduce((acc, m) => {
  const ultimo = acc[acc.length - 1];
  if (ultimo && ultimo.grupo === m.grupo) {
    ultimo.itens.push(m);
  } else {
    acc.push({ grupo: m.grupo, itens: [m] });
  }
  return acc;
}, []);

// Canais de atividade (historico de contatos — reusa comercial.Atendimento).
export const CANAIS_ATIVIDADE = [
  { value: "TELEFONE", label: "Telefone" },
  { value: "EMAIL", label: "E-mail" },
  { value: "WHATSAPP", label: "WhatsApp" },
  { value: "PRESENCIAL", label: "Presencial" },
  { value: "REUNIAO", label: "Reuniao" },
  { value: "OUTRO", label: "Outro" },
];

export const CANAL_ATIVIDADE_LABEL = CANAIS_ATIVIDADE.reduce((acc, c) => {
  acc[c.value] = c.label;
  return acc;
}, {});

// Tipos de compromisso na agenda do lead.
export const TIPOS_COMPROMISSO = [
  { value: "REUNIAO", label: "Reuniao" },
  { value: "VISITA", label: "Visita" },
  { value: "LIGACAO", label: "Ligacao" },
  { value: "OUTRO", label: "Outro" },
];

export const TIPO_COMPROMISSO_LABEL = TIPOS_COMPROMISSO.reduce((acc, t) => {
  acc[t.value] = t.label;
  return acc;
}, {});

// Estagios SPIN Selling + roteiro de perguntas adaptado a moveis planejados.
// Hard-coded no MVP (promover a configuravel via ConfiguracaoComercial depois).
// As `key` batem com LeadNota.categoria do backend (SITUACAO/PROBLEMA/...).
export const SPIN_ESTAGIOS = [
  {
    key: "SITUACAO",
    label: "Situação",
    descricao: "Entenda o contexto atual do cliente.",
    roteiro:
      "Como voces escolhem/compram os moveis hoje? Ja tem projeto/arquiteto? "
      + "Quais ambientes?",
  },
  {
    key: "PROBLEMA",
    label: "Problema",
    descricao: "Descubra as dores e insatisfacoes.",
    roteiro:
      "O que mais preocupa: prazo, acabamento, montagem, estourar o orçamento?",
  },
  {
    key: "IMPLICACAO",
    label: "Implicacao",
    descricao: "Amplifique o custo de não resolver.",
    roteiro:
      "Se a entrega/obra atrasa, o que isso custa (mudanca, aluguel estendido, "
      + "retrabalho, estresse)?",
  },
  {
    key: "NECESSIDADE",
    label: "Necessidade",
    descricao: "Faca o cliente verbalizar o ganho da solucao.",
    roteiro:
      "Se você tivesse o projeto sob medida entregue no prazo e dentro do "
      + "orçamento, o que mudaria pra você?",
  },
];

/**
 * Pipeline seed (7 etapas) — usado como FALLBACK quando a API de board
 * responde 404/erro, para a tela nunca quebrar durante a integracao.
 * As colunas vem vazias (leads: []) mas com a estrutura correta.
 *
 * Cores fixas vindas do design (NAO sao tokens de tema — sao a cor da
 * etapa configurada pelo gestor; o card/coluna pinta o dot com `etapa.cor`).
 */
export const PIPELINE_PADRAO = [
  { id: "seed-contato", nome: "Contato", cor: "#64748B", ordem: 0, tipo: "PENDENTE", is_default_entrada: true, ativo: true },
  { id: "seed-qualificacao", nome: "Qualificacao", cor: "#0EA5E9", ordem: 1, tipo: "EM_ANDAMENTO", is_default_entrada: false, ativo: true },
  { id: "seed-orcamento", nome: "Orçamento", cor: "#6366F1", ordem: 2, tipo: "EM_ANDAMENTO", is_default_entrada: false, ativo: true },
  { id: "seed-negociacao", nome: "Em negociação", cor: "#F59E0B", ordem: 3, tipo: "EM_ANDAMENTO", is_default_entrada: false, ativo: true },
  { id: "seed-aprovado", nome: "Aprovado", cor: "#10B981", ordem: 4, tipo: "EM_ANDAMENTO", is_default_entrada: false, ativo: true },
  { id: "seed-convertido", nome: "Convertido", cor: "#16A34A", ordem: 5, tipo: "FINALIZADO", is_default_entrada: false, ativo: true },
  { id: "seed-parado", nome: "Parado", cor: "#EF4444", ordem: 6, tipo: "PARADO", is_default_entrada: false, ativo: true },
];

// Board no shape esperado pelo componente: [{etapa, leads}] (colunas vazias).
function boardPadrao() {
  return PIPELINE_PADRAO.map((etapa) => ({
    etapa: {
      ...etapa,
      tipo_display: TIPO_ETAPA_LABEL[etapa.tipo] || etapa.tipo,
      leads_count: 0,
    },
    leads: [],
  }));
}

// ============================================================
// Filtros -> querystring
// ============================================================

/**
 * Valor do filtro `responsavel` que pede os leads SEM dono (FORM-06): o lead
 * do site nasce sem responsavel e o gestor precisa acha-los para atribuir.
 * Vira `?sem_responsavel=1` no backend.
 */
export const SEM_RESPONSAVEL = "SEM_RESPONSAVEL";

function montarQuery(filtros = {}) {
  const usp = new URLSearchParams();
  if (filtros.busca) usp.set("busca", filtros.busca);
  if (filtros.etapa) usp.set("etapa", filtros.etapa);
  if (filtros.responsavel === SEM_RESPONSAVEL) usp.set("sem_responsavel", "1");
  else if (filtros.responsavel) usp.set("responsavel", filtros.responsavel);
  if (filtros.origem) usp.set("origem", filtros.origem);
  // "meus" e resolvido pelo backend via request.user — so manda a flag.
  if (filtros.meus) usp.set("meus", "true");
  // Estados terminais: as telas /crm/{arquivados,perdidos,ganhos} precisam
  // enviar a flag explicita. Sem isto o backend cai no default (pipeline ativo)
  // e a tela mostra leads ATIVOS em vez dos terminais.
  if (filtros.arquivado) usp.set("arquivado", filtros.arquivado);
  if (filtros.perdido) usp.set("perdido", filtros.perdido);
  if (filtros.ganho) usp.set("ganho", filtros.ganho);
  // Periodo de entrada na etapa (datas locais YYYY-MM-DD, ja resolvidas pelo
  // board a partir do preset). Vai tambem para /pipeline-valor, que usa os
  // MESMOS filtros — sem isso o R$ do cabecalho contaria leads que o quadro
  // nao mostra.
  if (filtros.entrou_de) usp.set("entrou_de", filtros.entrou_de);
  if (filtros.entrou_ate) usp.set("entrou_ate", filtros.entrou_ate);
  // Abas Ganhos/Perdidos/Arquivados (CRM-45): periodo pela data do desfecho.
  if (filtros.de) usp.set("de", filtros.de);
  if (filtros.ate) usp.set("ate", filtros.ate);
  const qs = usp.toString();
  return qs ? `?${qs}` : "";
}

// ============================================================
// Leads
// ============================================================

/**
 * Board agrupado por etapa. Cai no PIPELINE_PADRAO (colunas vazias) se a API
 * responder erro — garante que a tela nunca quebra durante integracao.
 */
export async function listarBoard(request, filtros = {}) {
  try {
    const data = await request(`${PREFIXO_LEAD}/board${montarQuery(filtros)}`, {
      silentError: true,
    });
    if (Array.isArray(data)) return data;
    // Algumas APIs envelopam em {data:[...]} — desembrulha defensivamente.
    if (Array.isArray(data?.data)) return data.data;
    return boardPadrao();
  } catch {
    return boardPadrao();
  }
}

export function listarLeads(request, filtros = {}, opcoes = {}) {
  return request(`${PREFIXO_LEAD}${montarQuery(filtros)}`, opcoes);
}

/**
 * Linhas da exportacao do CRM (CRM-35): o lead inteiro, sem paginacao, com
 * os MESMOS filtros do board. O board segue leve (LeadListaSerializer);
 * aqui vem LeadExportSerializer: empresa, cargo, contatos secundarios,
 * redes, nascimento, bio, endereco principal (cidade/estado/cep/linha) e
 * parceiros (lista + `parceiros_nomes`).
 */
export function exportarLeads(request, filtros = {}) {
  return request(`${PREFIXO_LEAD}/exportar${montarQuery(filtros)}`);
}

/**
 * Valor do pipeline (R$ do cabecalho do board) pela FONTE configurada no
 * painel comercial (ConfiguracaoComercial.layout_painel.fontes.pipeline).
 * Computado pelo backend sobre os MESMOS leads do board (mesmos filtros),
 * mantendo N e R$ consistentes. Painel comercial configuravel — Fase 5.
 *
 * Retorna {soma: string, qtd: number, fonte: string} ou `null` em erro —
 * o cabecalho cai no fallback (Σ valor_estimado somado no frontend).
 */
export async function obterPipelineValor(request, filtros = {}) {
  try {
    const data = await request(
      `${PREFIXO_LEAD}/pipeline-valor${montarQuery(filtros)}`,
      { silentError: true }
    );
    if (data && data.soma !== undefined) return data;
    return null;
  } catch {
    return null;
  }
}

export function obterLead(request, id) {
  return request(`${PREFIXO_LEAD}/${id}`);
}

/**
 * POST /api/crm/leads — so `titulo` e obrigatorio; devolve o Lead detalhe (201).
 *
 * `enderecos` (ADR-0071, opcional): lista de enderecos titulados, o 1o e o
 * principal. Item: `{titulo, cep, logradouro, numero, complemento, bairro,
 * cidade, estado}` — Lead nao tem `cobranca`. O CRM monta a lista com
 * `prepararEnderecos` (pages/Crm/enderecosLead.js). Endereco recusado responde
 * 400 `{detalhe, enderecos}`: um objeto por item enviado (`{}` = ok,
 * `{campo: [mensagens]}`) ou mensagens da lista inteira (strings).
 */
export function criarLead(request, dados, options = {}) {
  return request(PREFIXO_LEAD, {
    method: "POST",
    body: JSON.stringify(dados),
    // `options` repassa ao `request`: com `{silentError: true}` o form mostra
    // o erro por campo e o toast automatico nao repete o aviso (CRM-40).
    ...options,
  });
}

/**
 * Move o lead para outra etapa. O backend valida transicoes terminais:
 *   - etapa PERDIDO exige `razao_perda` (400 {detalhe} se faltar)
 *   - etapa ORCAMENTO aceita `criar_orcamento: true` para disparar o orcamento
 * Retorna LeadDetalhe atualizado.
 */
export function moverLead(
  request,
  id,
  { etapa_destino, razao_perda, descricao_perda, criar_orcamento } = {}
) {
  const body = { etapa_destino };
  if (razao_perda !== undefined) body.razao_perda = razao_perda;
  if (descricao_perda !== undefined) body.descricao_perda = descricao_perda;
  if (criar_orcamento !== undefined) body.criar_orcamento = criar_orcamento;
  return request(`${PREFIXO_LEAD}/${id}/mover`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function arquivarLead(request, id) {
  return request(`${PREFIXO_LEAD}/${id}/arquivar`, { method: "POST" });
}

export function desarquivarLead(request, id) {
  return request(`${PREFIXO_LEAD}/${id}/desarquivar`, { method: "POST" });
}

// Registra a perda do lead (motivo + detalhe livre). O lead sai do board ativo
// e passa para a aba "Perdidos" — reversivel via `reativarLead`.
export function perderLead(request, id, { motivo, detalhe = "" }) {
  // silentError: o RegistrarPerdaDialog mostra o erro inline (WarningMsg) —
  // suprime o auto-toast global para nao duplicar a mensagem.
  return request(`${PREFIXO_LEAD}/${id}/perder`, {
    method: "POST",
    body: JSON.stringify({ motivo, detalhe }),
    silentError: true,
  });
}

// Desfaz a perda — devolve o lead ao board (etapa "Parado" se houver).
export function reativarLead(request, id) {
  return request(`${PREFIXO_LEAD}/${id}/reativar`, { method: "POST" });
}

export function excluirLead(request, id) {
  // R22: hard delete. Backend gateia com lead:delete + MFA recente; o
  // interceptor global do AuthContext trata o step-up de MFA transparente.
  return request(`${PREFIXO_LEAD}/${id}`, { method: "DELETE" });
}

export function atribuirLead(request, id, responsavel) {
  return request(`${PREFIXO_LEAD}/${id}/atribuir`, {
    method: "POST",
    body: JSON.stringify({ responsavel }),
  });
}

// ID 26: sincroniza os parceiros indicadores do lead (M2M). Recebe a lista
// COMPLETA de ids de Representante; o backend cria os que faltam e remove os
// que sobram. Retorna o lead detalhado (com `parceiros` atualizados).
export function sincronizarParceirosLead(request, id, parceiros) {
  return request(`${PREFIXO_LEAD}/${id}/parceiros`, {
    method: "POST",
    body: JSON.stringify({ parceiros }),
  });
}

// ============================================================
// Etapas do pipeline (CRUD direto — usado pontualmente; o fluxo principal
// de configuracao usa /pipeline/configurar em lote)
// ============================================================

export function listarEtapas(request) {
  return request(PREFIXO_ETAPA);
}

export function criarEtapa(request, dados) {
  return request(PREFIXO_ETAPA, {
    method: "POST",
    body: JSON.stringify(dados),
  });
}

export function atualizarEtapa(request, id, dados) {
  return request(`${PREFIXO_ETAPA}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dados),
  });
}

export function deletarEtapa(request, id) {
  return request(`${PREFIXO_ETAPA}/${id}`, { method: "DELETE" });
}

export function reordenarEtapas(request, ordemIds) {
  return request(`${PREFIXO_ETAPA}/reordenar`, {
    method: "POST",
    body: JSON.stringify({ ordem_ids: ordemIds }),
  });
}

// ============================================================
// Pipeline (etapas + regras globais)
// ============================================================

/**
 * Retorna {etapas:[...], regras:{...}}. Cai num default seguro em erro para
 * a tela de configuracao nao quebrar (mesma filosofia do listarBoard).
 */
function pipelineDegradado() {
  return {
    etapas: PIPELINE_PADRAO,
    grupos: [],
    regras: {
      // CRM-23: FAIL-SAFE. Estas `regras` alimentam o gate de arrastar do
      // board (`podeMoverLead`), que trata regra ausente como DESLIGADA para
      // nao deixar arrastar e levar 403. Prometer "vendedor pode mover" sem
      // ter falado com o servidor desfazia isso no caminho degradado.
      pipeline_permite_vendedor_mover: false,
      pipeline_notificar_gerente_dias: 14,
      pipeline_auto_arquivar_perdidos_dias: 90,
    },
    // Marca para a tela distinguir "regra desligada" de "nao carregou".
    _fallback: true,
  };
}

export async function obterPipeline(request) {
  try {
    const data = await request(PREFIXO_PIPELINE, { silentError: true });
    if (data && Array.isArray(data.etapas)) return data;
    return pipelineDegradado();
  } catch {
    return pipelineDegradado();
  }
}

/**
 * Salva o pipeline inteiro de uma vez (etapas + regras). Aplicacao global,
 * registrada em audit log no backend. Retorna {etapas, regras, leads_remapeados}.
 */
export function configurarPipeline(request, { etapas, grupos, regras, remapeamentos }) {
  return request(`${PREFIXO_PIPELINE}/configurar`, {
    method: "POST",
    body: JSON.stringify({
      etapas,
      grupos: grupos || [],
      regras,
      remapeamentos: remapeamentos || {},
    }),
  });
}

// ============================================================
// Cockpit do Lead (ADR-0041) — detalhe expandido + acoes
// ============================================================

/**
 * PATCH parcial dos dados do lead. Body so com os campos a alterar
 * (titulo, contato_*, temperatura, status, tags[], empresa, cargo,
 * enderecos, contatos extras, data_nascimento, descricao_bio, valor_estimado,
 * origem, proxima_acao, data_proxima_acao, observacoes). Retorna o Lead
 * detalhe atualizado.
 *
 * `enderecos` (ADR-0071) e a lista COMPLETA, na ordem (o 1o e o principal):
 * item com `id` do lead atualiza, sem `id` cria, endereco ausente da lista e
 * apagado; `[]` apaga todos. Chave ausente = nao mexe — por isso o cockpit so
 * a envia quando a lista mudou. Item: `{id?, titulo, cep, logradouro, numero,
 * complemento, bairro, cidade, estado}`. Nao existem mais `cidade`/`estado`/
 * `cep` soltos no lead (o backend ignoraria). 400 no mesmo formato de
 * `criarLead`.
 *
 * Leitura: `lead.enderecos[]` traz tambem `ordem`, `principal` e
 * `linha_unica` (calculados; ignorados se reenviados).
 */
/**
 * Contato ja cadastrado na unidade (CRM-12): clientes ativos e leads abertos
 * com o mesmo e-mail, telefone (por digitos) ou CPF/CNPJ.
 * Resposta: `{clientes: [{id, codigo, nome, email, telefone, documento,
 * coincide}], leads: [{id, numero, titulo, contato_nome, contato_email,
 * contato_telefone, documento, etapa_nome, responsavel_nome, coincide}]}`.
 * `silentError`: e um aviso — falha da consulta nao pode travar a tela.
 */
export function possiveisDuplicados(
  request,
  { email = "", telefone = "", documento = "", excluir_lead, excluir_cliente } = {}
) {
  const params = new URLSearchParams();
  if (email) params.set("email", email);
  if (telefone) params.set("telefone", telefone);
  if (documento) params.set("documento", documento);
  if (excluir_lead) params.set("excluir_lead", excluir_lead);
  if (excluir_cliente) params.set("excluir_cliente", excluir_cliente);
  if (!params.toString()) return Promise.resolve({ clientes: [], leads: [] });
  return request(`/api/crm/possiveis-duplicados?${params.toString()}`, { silentError: true });
}

export function atualizarLead(request, id, campos) {
  return request(`${PREFIXO_LEAD}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(campos),
  });
}

/** Timeline unificada (eventos + atendimentos + compromissos), desc. */
export function obterTimeline(request, id) {
  return request(`${PREFIXO_LEAD}/${id}/timeline`);
}

/**
 * Prende uma observacao a UM registro da timeline (evento/atendimento/
 * compromisso). `registro_id` e' o `id` do item da timeline. Devolve a
 * timeline inteira atualizada.
 */
export function comentarRegistroTimeline(request, id, { origem, registro_id, texto }) {
  return request(`${PREFIXO_LEAD}/${id}/timeline/comentar`, {
    method: "POST",
    body: JSON.stringify({ origem, registro_id, texto }),
  });
}

/** Mini-dashboard do lead (dias no pipeline/etapa, contatos, SPIN, ...). */
export function obterMetricas(request, id) {
  return request(`${PREFIXO_LEAD}/${id}/metricas`);
}

/** Historico de atendimentos (contatos) do lead. */
export function listarAtividades(request, id) {
  return request(`${PREFIXO_LEAD}/${id}/atividades`);
}

/**
 * Registra uma atividade (chamada/contato) — cria Atendimento + evento.
 * Campos: canal (default TELEFONE), tipo (default FOLLOW_UP), assunto*,
 * descricao, resultado (default PENDENTE), proxima_acao, data_proxima_acao,
 * data_atendimento. Retorna o Lead detalhe atualizado (201).
 */
export function registrarAtividade(request, id, dados) {
  return request(`${PREFIXO_LEAD}/${id}/atividades`, {
    method: "POST",
    body: JSON.stringify(dados),
  });
}

/**
 * Cria um orcamento a partir do lead.
 *
 * - `cliente_id`: vincula um Cliente ja cadastrado. Lead que ja tinha OUTRO
 *   cliente leva os orcamentos nao convertidos junto (CRM-19).
 * - Sem `cliente_id`, o backend cria o Cliente a partir do lead (completo:
 *   nascimento, telefone 2, cargo, tags, observacoes, enderecos — CRM-03),
 *   com `tipo` "PF" | "PJ" (CRM-14; PJ usa `lead.empresa` como nome) e os
 *   campos de contato (`contato_nome`, `contato_telefone`, `contato_email`)
 *   gravados no lead ANTES de criar — Transition Form (CRM-18). Sem nome de
 *   contato o backend responde 400 {detalhe}.
 * - `parceiros` (Orcamento v2): [{representante: id, observacao?}] — RT
 *   herdado do cadastro do Representante.
 *
 * `silentError`: o CriarOrcamentoDialog mostra o erro por campo/inline.
 * Retorna o Orcamento detalhe (o frontend redireciona para /orcamentos/{id}).
 */
export function criarOrcamentoDoLead(
  request,
  id,
  {
    cliente_id, parceiros, tipo, contato_nome, contato_telefone, contato_email,
    documento, criar_mesmo_assim,
  } = {}
) {
  const body = {};
  if (cliente_id) body.cliente_id = cliente_id;
  if (tipo) body.tipo = tipo;
  if (contato_nome) body.contato_nome = contato_nome;
  if (contato_telefone) body.contato_telefone = contato_telefone;
  if (contato_email) body.contato_email = contato_email;
  if (documento) body.documento = documento;
  // CRM-12: o 409 com candidatos so e vencido por escolha explicita.
  if (criar_mesmo_assim) body.criar_mesmo_assim = true;
  if (Array.isArray(parceiros) && parceiros.length) body.parceiros = parceiros;
  return request(`${PREFIXO_LEAD}/${id}/criar-orcamento`, {
    method: "POST",
    body: JSON.stringify(body),
    silentError: true,
  });
}

/**
 * Envia e-mail ao contato do lead (exige contato_email; senao 400).
 * `silentError` porque o EnviarEmailDialog mostra o erro inline (evita
 * duplicar a mensagem com o auto-toast global do AuthContext).
 */
export function enviarEmailLead(request, id, { assunto, corpo }) {
  return request(`${PREFIXO_LEAD}/${id}/enviar-email`, {
    method: "POST",
    body: JSON.stringify({ assunto, corpo }),
    silentError: true,
  });
}

/**
 * Envia WhatsApp pelo numero do sistema (no nome do vendedor). Sem instancia
 * conectada o backend retorna 400 {detalhe:"WhatsApp nao conectado..."} — a
 * UI trata exibindo o aviso inline. `silentError` evita duplicar a mensagem
 * com o auto-toast global.
 */
export function enviarWhatsappLead(request, id, { texto }) {
  return request(`${PREFIXO_LEAD}/${id}/enviar-whatsapp`, {
    method: "POST",
    body: JSON.stringify({ texto }),
    silentError: true,
  });
}

// ---------- Notas (SPIN + GERAL) ----------

export function listarNotas(request, { lead, categoria } = {}) {
  const usp = new URLSearchParams();
  if (lead) usp.set("lead", lead);
  if (categoria) usp.set("categoria", categoria);
  const qs = usp.toString();
  return request(`${PREFIXO_NOTA}${qs ? `?${qs}` : ""}`);
}

export function criarNota(request, { lead, categoria = "GERAL", texto, fixada = false }) {
  return request(PREFIXO_NOTA, {
    method: "POST",
    body: JSON.stringify({ lead, categoria, texto, fixada }),
  });
}

export function atualizarNota(request, id, campos) {
  return request(`${PREFIXO_NOTA}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(campos),
  });
}

export function excluirNota(request, id) {
  return request(`${PREFIXO_NOTA}/${id}`, { method: "DELETE" });
}

export function fixarNota(request, id, fixada) {
  return request(`${PREFIXO_NOTA}/${id}/fixar`, {
    method: "POST",
    body: JSON.stringify({ fixada }),
  });
}

// ---------- Tarefas + subtarefas ----------

export function listarTarefas(request, { lead } = {}) {
  const qs = lead ? `?lead=${lead}` : "";
  return request(`${PREFIXO_TAREFA}${qs}`);
}

export function criarTarefa(request, dados) {
  return request(PREFIXO_TAREFA, {
    method: "POST",
    body: JSON.stringify(dados),
  });
}

export function atualizarTarefa(request, id, campos) {
  return request(`${PREFIXO_TAREFA}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(campos),
  });
}

export function excluirTarefa(request, id) {
  return request(`${PREFIXO_TAREFA}/${id}`, { method: "DELETE" });
}

export function toggleTarefa(request, id, concluida) {
  return request(`${PREFIXO_TAREFA}/${id}/toggle`, {
    method: "POST",
    body: JSON.stringify({ concluida }),
  });
}

export function criarSubtarefa(request, tarefaId, { titulo }) {
  return request(`${PREFIXO_TAREFA}/${tarefaId}/subtarefas`, {
    method: "POST",
    body: JSON.stringify({ titulo }),
  });
}

export function atualizarSubtarefa(request, id, campos) {
  return request(`${PREFIXO_SUBTAREFA}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(campos),
  });
}

export function excluirSubtarefa(request, id) {
  return request(`${PREFIXO_SUBTAREFA}/${id}`, { method: "DELETE" });
}

export function toggleSubtarefa(request, id, concluida) {
  return request(`${PREFIXO_SUBTAREFA}/${id}/toggle`, {
    method: "POST",
    body: JSON.stringify({ concluida }),
  });
}

// ---------- Anexos (multipart) ----------

// `opcoes` existe para a tela pedir `silentError` quando ela mesma mostra a
// mensagem (o AnexosBlock mostra no bloco e no diálogo de exclusão) — sem isso
// o auto-toast do `request` repete o erro que já está na tela.
export function listarAnexosLead(request, leadId, opcoes = {}) {
  return request(`${PREFIXO_ANEXO}?lead=${leadId}`, opcoes);
}

export function uploadAnexoLead(request, { leadId, arquivo, descricao = "" }, opcoes = {}) {
  const formData = new FormData();
  formData.append("lead", leadId);
  formData.append("arquivo", arquivo);
  if (descricao) formData.append("descricao", descricao);
  return request(PREFIXO_ANEXO, {
    ...opcoes,
    method: "POST",
    body: formData,
  });
}

export function excluirAnexoLead(request, id, opcoes = {}) {
  return request(`${PREFIXO_ANEXO}/${id}`, { ...opcoes, method: "DELETE" });
}

// ---------- Compromissos (agenda do lead) ----------

export function listarCompromissos(request, { lead } = {}) {
  const qs = lead ? `?lead=${lead}` : "";
  return request(`${PREFIXO_COMPROMISSO}${qs}`);
}

export function criarCompromisso(request, dados) {
  return request(PREFIXO_COMPROMISSO, {
    method: "POST",
    body: JSON.stringify(dados),
  });
}

export function atualizarCompromisso(request, id, campos) {
  return request(`${PREFIXO_COMPROMISSO}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(campos),
  });
}

export function excluirCompromisso(request, id) {
  return request(`${PREFIXO_COMPROMISSO}/${id}`, { method: "DELETE" });
}

export function marcarCompromissoRealizado(request, id, realizado) {
  return request(`${PREFIXO_COMPROMISSO}/${id}/realizar`, {
    method: "POST",
    body: JSON.stringify({ realizado }),
  });
}
